import { z } from "zod";

/**
 * What the shopper can do by voice. Speech is transcribed on the server
 * (packages/voice), which detects the language; live voice is the call
 * service (apps/voice).
 */
export const voiceSchema = z
	.object({
		/** Mic in the message box: speech becomes editable text. */
		dictation: z.boolean().default(true),
		/** Hold to record, sent with its transcript. */
		voiceNotes: z.boolean().default(true),
		/** Hands-free conversation: listen, answer out loud, repeat. */
		live: z.boolean().default(true),
		/** Tell the shopper once, before their first call, that live voice is in beta. */
		betaNotice: z.boolean().default(true),
		/**
		 * Who hears and speaks Arabic, in voice notes and calls. Hamsa is Lebanese only; another
		 * dialect (Gulf, Egyptian, MSA) is ElevenLabs: Scribe v2 hears it, Eleven v4 Turbo
		 * speaks it in `voiceId`, a voice from its library. What the assistant writes follows
		 * `languages.rules` either way.
		 */
		arabic: z
			.object({
				engine: z.enum(["hamsa", "elevenlabs"]).default("hamsa"),
				voiceId: z.string().trim().max(64).default(""),
			})
			.prefault({}),
		/** Shoppers calling the assistant on the retailer's WhatsApp number. */
		calls: z
			.object({
				/** Said once the call connects. {assistant} and {store} are filled in. */
				greeting: z
					.string()
					.trim()
					.min(1)
					.max(200)
					.default(
						"Hi, this is {assistant} from {store}. How can I help?",
					),
				/** Say, after the greeting, that the caller is talking to an AI assistant. */
				aiNotice: z.boolean().default(true),
				/** Said before anything else when the retailer records calls. */
				recordingNotice: z
					.string()
					.trim()
					.min(1)
					.max(200)
					.default("This call is recorded."),
				/** Seconds without a word from the caller before the assistant ends the call. */
				silenceSeconds: z.number().int().min(5).max(120).default(20),
			})
			.prefault({}),
	})
	.prefault({});

/**
 * The Arabic the fixed words around the agent are in (call lines, the chat's own wording,
 * suggested replies): Lebanese where Hamsa speaks the retailer's Arabic, Gulf where the retailer
 * picked an ElevenLabs voice for another dialect. What the agent writes follows `languages.rules`.
 */
export type ArabicDialect = "lebanese" | "gulf";

export function arabicDialect(voice: {
	arabic: { engine: "hamsa" | "elevenlabs"; voiceId: string };
}): ArabicDialect {
	return voice.arabic.engine === "elevenlabs" && voice.arabic.voiceId
		? "gulf"
		: "lebanese";
}
